import { useMemo, useState } from 'react';
import { Box, Button, Link, Paper, Skeleton, Stack, Typography } from '@mui/material';
import { formatNumber } from '~/utils/formatNumber.js';
import redirectOnAnalytics from '~/utils/redirectOnAnalytics.js';
import { geographyLevel, getCountryGeography } from './utils/geographyTransform.js';
import getCurrencySymbol from '~/utils/getCurrencySymbol.js';
import AppConfig from '~/config/AppConfig.js';

export default function TopRegions({
  selectedCountry,
  isLoading = false,
  hasError = false,
  startDate,
  endDate,
}) {
  const [selectedLevel, setSelectedLevel] = useState(geographyLevel.region);
  const otherCountryNames = new Set(['Other', 'Others']);
  const isOtherCountry = selectedCountry && otherCountryNames.has(selectedCountry.name?.trim());
  const effectiveSelectedLevel = isOtherCountry ? geographyLevel.region : selectedLevel;

  const rows = useMemo(() => {
    if (isOtherCountry) {
      return [];
    }

    return getCountryGeography(selectedCountry, effectiveSelectedLevel);
  }, [selectedCountry, effectiveSelectedLevel, isOtherCountry]);

  const isCitySelected = effectiveSelectedLevel === geographyLevel.city;
  const titleEntity = isCitySelected ? 'cities' : 'regions';

  const titleText = selectedCountry
    ? `Top 10 ${titleEntity} of ${selectedCountry.name}`
    : `Top 10 ${titleEntity}`;

  const subtitleText =
    selectedCountry || hasError
      ? 'Based on ad inventory'
      : 'Select a country from Country to view data';

  return (
    <Paper
      elevation={2}
      className="sas-top-regions-card">
      <Stack
        direction="row"
        spacing={1}
        className="sas-top-regions-toolbar">
        <Box>
          {isLoading ? (
            <>
              <Skeleton
                variant="text"
                width={190}
                height={28}
              />
              <Skeleton
                variant="text"
                width={120}
                height={20}
              />
            </>
          ) : (
            <>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 600 }}
                component="h3">
                {titleText}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary">
                {subtitleText}
              </Typography>
            </>
          )}
        </Box>

        {!isOtherCountry ? (
          <Stack
            direction="row"
            className="sas-top-regions-toggle">
            <Button
              size="small"
              disabled={isLoading}
              onClick={() => setSelectedLevel(geographyLevel.region)}
              sx={{
                color:
                  effectiveSelectedLevel === geographyLevel.region
                    ? 'primary.main'
                    : 'text.primary',
                bgcolor:
                  effectiveSelectedLevel === geographyLevel.region ? '#e9efff' : 'transparent',
              }}>
              Region
            </Button>
            <Button
              size="small"
              disabled={isLoading || isOtherCountry}
              onClick={() => setSelectedLevel(geographyLevel.city)}
              sx={{
                color:
                  effectiveSelectedLevel === geographyLevel.city ? 'primary.main' : 'text.primary',
                bgcolor: effectiveSelectedLevel === geographyLevel.city ? '#e9efff' : 'transparent',
              }}>
              City
            </Button>
          </Stack>
        ) : null}
      </Stack>

      <Box className="sas-top-regions-content">
        {isLoading ? (
          <Stack spacing={1}>
            {Array.from({ length: 5 }).map((_, index) => (
              <Skeleton
                key={index}
                variant="rounded"
                height={63}
              />
            ))}
          </Stack>
        ) : null}

        {!isLoading && !hasError && !selectedCountry ? (
          <Box className="sas-top-regions-empty">
            <Box>
              <Typography
                variant="subtitle1"
                component="p">
                No country selected yet
              </Typography>
              <Typography color="text.secondary">
                Choose a country from Country to view top data
              </Typography>
            </Box>
          </Box>
        ) : null}

        {!isLoading && (hasError || (selectedCountry && rows.length === 0)) ? (
          <Box className="sas-top-regions-empty">
            <Box>
              <Typography
                variant="subtitle1"
                component="p">
                No {isCitySelected ? 'city' : 'region'} data is available yet
              </Typography>
              <Typography color="text.secondary">Data will appear here once available</Typography>
            </Box>
          </Box>
        ) : null}

        {!isLoading && !hasError && rows.length > 0 ? (
          <Stack spacing={1}>
            {rows.map(item => (
              <Paper
                elevation={2}
                key={item.id}
                className="sas-top-regions-row">
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="subtitle2"
                    component="p">
                    {item.name}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    className="sas-top-regions-row-meta">
                    {`${formatNumber(item.metrics['Ad Requests'])} Ad Requests | ${formatNumber(item.metrics.Impressions)} Impr | ${formatNumber(item.metrics.Clicks)} Clicks`}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                  <Typography
                    variant="subtitle2"
                    component="p">
                    {`${getCurrencySymbol()} ${formatNumber(item.metrics.Revenue)}`}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary">
                    Revenue
                  </Typography>
                </Box>
              </Paper>
            ))}
          </Stack>
        ) : null}
      </Box>

      <Box className="sas-report-link-wrap">
        <Link
          component="button"
          className="sas-report-link"
          onClick={() =>
            redirectOnAnalytics({ groups: AppConfig.geographyReportGroups, startDate, endDate })
          }>
          OPEN GEOGRAPHY REPORT -&gt;
        </Link>
      </Box>
    </Paper>
  );
}
